import Quill, { Parchment } from 'quill/core';
import React, { useRef, useState } from 'react';
import ToolbarButton from './ToolbarButton';
import * as styles from './ToolbarObjectButton.module.scss';
import DropdownMenu from '../../../../components/DropdownMenu';
import { MenuItemRadio } from '../../../../components/Menu';
import SVGIcon from '../../../../components/SVGIcon';
import { useQuill } from '../../../QuillContext';
import { textIcon } from '../../editorIcons';
import {
  BlockquoteItem,
  BulletedListItem,
  ChecklistItem,
  Heading1Item,
  Heading2Item,
  Heading3Item,
  HintDangerItem,
  HintInfoItem,
  HintSuccessItem,
  HintWarningItem,
  NumberedListItem,
} from '../../getSlashCommandMenuSections';

const PlainTextItem = {
  id: 'plain',
  data: {
    label: 'Normal text',
    icon: <SVGIcon>{textIcon}</SVGIcon>,
  },
};

const labelToFormat = {
  [Heading1Item.data.label]: { name: 'header', value: 1 },
  [Heading2Item.data.label]: { name: 'header', value: 2 },
  [Heading3Item.data.label]: { name: 'header', value: 3 },
  [BulletedListItem.data.label]: { name: 'list', value: 'bullet' },
  [NumberedListItem.data.label]: { name: 'list', value: 'ordered' },
  [ChecklistItem.data.label]: { name: 'list', value: 'unchecked' },
  [HintInfoItem.data.label]: { name: 'hint', value: 'info' },
  [HintSuccessItem.data.label]: { name: 'hint', value: 'success' },
  [HintWarningItem.data.label]: { name: 'hint', value: 'warning' },
  [HintDangerItem.data.label]: { name: 'hint', value: 'error' },
  [BlockquoteItem.data.label]: { name: 'blockquote', value: true },
  [HintInfoItem.data.label]: { name: 'hint', value: 'info' },
  [HintDangerItem.data.label]: { name: 'hint', value: 'error' },
  [HintSuccessItem.data.label]: { name: 'hint', value: 'success' },
  [HintWarningItem.data.label]: { name: 'hint', value: 'warning' },
};

export interface ToolbarObjectButtonOptions {
  defaultLabel: string;
  queryFormat(formatName: string): unknown;
  applyFormat(
    format: (typeof labelToFormat)[keyof typeof labelToFormat] | null,
  ): void;
}

interface ToolbarObjectButtonProps {
  disabled: boolean;
  options?: ToolbarObjectButtonOptions;
  initialDropdownOpen?: boolean;
}

const ToolbarObjectButton = ({
  disabled,
  options,
  initialDropdownOpen,
}: ToolbarObjectButtonProps) => {
  const {
    quill,
    selection: { formats },
  } = useQuill();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [index, setIndex] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(initialDropdownOpen);

  const queryFormat: ToolbarObjectButtonOptions['queryFormat'] =
    options?.queryFormat || ((formatName) => quill?.scroll.query(formatName));

  const applyFormat: ToolbarObjectButtonOptions['applyFormat'] =
    options?.applyFormat ||
    ((format) => {
      if (format) {
        quill?.format(format.name, format.value, Quill.sources.USER);
      } else {
        Object.keys(formats)
          .filter((name) => quill?.scroll.query(name, Parchment.Scope.BLOCK))
          .forEach((name) => quill?.format(name, null, Quill.sources.USER));
      }
    });

  const isCheckList =
    formats.list === 'checked' ||
    formats.list === 'unchecked' ||
    (Array.isArray(formats.list) &&
      formats.list.toSorted().join('') === 'checkedunchecked');

  const activeLabel = isCheckList
    ? ChecklistItem.data.label
    : Object.keys(labelToFormat).find((key) => {
        const format = labelToFormat[key];
        if (!format) return false;
        const { name, value } = format;
        return formats[name] && (!value || formats[name] === value);
      }) ||
      options?.defaultLabel ||
      PlainTextItem.data.label;

  const items = [
    Heading1Item,
    Heading2Item,
    Heading3Item,
    BulletedListItem,
    NumberedListItem,
    ChecklistItem,
    HintInfoItem,
    HintSuccessItem,
    HintWarningItem,
    HintDangerItem,
    BlockquoteItem,
    PlainTextItem,
  ].filter((item) => {
    const format = labelToFormat[item.data.label];
    return !format || !quill || queryFormat(format.name);
  });

  return (
    <ToolbarButton
      ref={buttonRef}
      disabled={disabled}
      renderPopOver={(onClose) => (
        <div className={styles.popOver}>
          <DropdownMenu.Root
            index={index}
            withPadding
            onIndexChange={setIndex}
            onClose={onClose}
            onSelect={({ data: { label } }) => {
              const format = labelToFormat[label];
              applyFormat(format || null);
              onClose();
            }}
            maxHeight="none"
            inputContainer={buttonRef.current}
            sections={[{ items }]}
            renderItem={(data) => (
              <MenuItemRadio
                icon={data.icon}
                checked={data.label === activeLabel}
              >
                {data.label}
              </MenuItemRadio>
            )}
          />
        </div>
      )}
      popOverOpen={isDropdownOpen}
      onPopOverOpenChange={(open) => {
        setIsDropdownOpen(open);
        if (!open) setIndex(null);
      }}
      aria-label="Paragraph style"
    >
      <div className={styles.label}>{activeLabel}</div>
    </ToolbarButton>
  );
};

export default ToolbarObjectButton;
